import React, { useEffect, useRef, useState, Suspense } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AI_ROLE, USER_ROLE } from "../../../constants/ai-tutor";

const LazyPrism = React.lazy(async () => {
  const mod = await import("react-syntax-highlighter");
  return { default: mod.Prism };
});
const loadPrismTheme = () =>
  import("react-syntax-highlighter/dist/esm/styles/prism").then(
    (m) => m.tomorrow
  );

function CodeBlock({ inline, className, children, ...props }) {
  const match = /language-(\w+)/.exec(className || "");
  const [theme, setTheme] = useState(null);

  useEffect(() => {
    if (!inline && match && !theme) {
      loadPrismTheme()
        .then(setTheme)
        .catch(() => {});
    }
  }, [inline, match, theme]);

  if (inline || !match) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  }

  if (!theme) {
    return (
      <pre className="markdown-pre">
        <code className={className}>{String(children).replace(/\n$/, "")}</code>
      </pre>
    );
  }

  return (
    <Suspense
      fallback={
        <pre className="markdown-pre">
          <code className={className}>
            {String(children).replace(/\n$/, "")}
          </code>
        </pre>
      }
    >
      <LazyPrism style={theme} language={match[1]} PreTag="div" {...props}>
        {String(children).replace(/\n$/, "")}
      </LazyPrism>
    </Suspense>
  );
}

const markdownComponents = {
  code: CodeBlock,
  h1: ({ children }) => <h1 className="markdown-h1">{children}</h1>,
  h2: ({ children }) => <h2 className="markdown-h2">{children}</h2>,
  h3: ({ children }) => <h3 className="markdown-h3">{children}</h3>,
  h4: ({ children }) => <h4 className="markdown-h4">{children}</h4>,
  h5: ({ children }) => <h5 className="markdown-h5">{children}</h5>,
  h6: ({ children }) => <h6 className="markdown-h6">{children}</h6>,
  p: ({ children }) => <p className="markdown-p">{children}</p>,
  ul: ({ children }) => <ul className="markdown-ul">{children}</ul>,
  ol: ({ children }) => <ol className="markdown-ol">{children}</ol>,
  li: ({ children }) => <li className="markdown-li">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="markdown-blockquote">{children}</blockquote>
  ),
  strong: ({ children }) => <strong className="markdown-strong">{children}</strong>,
  em: ({ children }) => <em className="markdown-em">{children}</em>,
  a: ({ href, children }) => (
    <a href={href} className="markdown-link" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  table: ({ children }) => <table className="markdown-table">{children}</table>,
  thead: ({ children }) => <thead className="markdown-thead">{children}</thead>,
  tbody: ({ children }) => <tbody className="markdown-tbody">{children}</tbody>,
  tr: ({ children }) => <tr className="markdown-tr">{children}</tr>,
  th: ({ children }) => <th className="markdown-th">{children}</th>,
  td: ({ children }) => <td className="markdown-td">{children}</td>,
};

export const RenderMarkdownOrText = ({ message, stringContent, streamingNow }) => {
  const [markdownEnabled, setMarkdownEnabled] = useState(!streamingNow);
  useEffect(() => {
    if (streamingNow) {
      setMarkdownEnabled(false);
      return;
    }
    const id = setTimeout(() => setMarkdownEnabled(true), 200);
    return () => clearTimeout(id);
  }, [streamingNow]);

  const mdCacheRef = useRef(new Map());
  const hasMarkdown = /[#*`\[\]()>|~]/.test(String(stringContent || ""));
  const isAI = message?.role === AI_ROLE;

  const renderContent = () => {
    if (streamingNow || !markdownEnabled || !hasMarkdown || !isAI) {
      return (
        <div className={`message-text ${streamingNow ? "streaming" : ""}`}>
          {String(stringContent ?? "")}
        </div>
      );
    }

    const cacheKey = `${message?.id}|${stringContent}|mdOn`;
    const cached = mdCacheRef.current.get(cacheKey);
    if (cached) return cached;

    const rendered = (
      <ReactMarkdown
        components={markdownComponents}
        remarkPlugins={[remarkGfm]}
        className="markdown-content"
      >
        {String(stringContent ?? "")}
      </ReactMarkdown>
    );
    mdCacheRef.current.set(cacheKey, rendered);
    return rendered;
  };

  return renderContent();
};

export const MarkdownOrTextComponent = (message, stringContent, streamingNow) => {
  return (
    <RenderMarkdownOrText
      message={message}
      stringContent={stringContent}
      streamingNow={!!streamingNow}
    />
  );
};